import React, { useState, ComponentPropsWithoutRef, useEffect, useRef, Ref } from 'react';
import { debounce, isEqual } from 'lodash';
import { BoloAutocompleteView, BoloResource } from '@mark43/cad-api';
import { useApiClient } from '~/api-client';
import { omitRef } from '../helpers/omit-ref';
import type { OnInputChangeParams } from './types';
import { useFocusManager } from '@react-aria/focus';
import { moveFocusNext } from '../helpers/move-focus';
import { BaseSelect } from './base-select';
import { SelectInstance } from 'react-select';
import { GroupBase } from '@cad-arc';

export type BoloOption = {
    value: number;
    label: string;
    render: () => React.ReactElement;
    data: BoloAutocompleteView;
};

interface BoloAutocompleteSelectProps
    extends Omit<
        ComponentPropsWithoutRef<typeof BaseSelect>,
        'options' | 'isLoading' | 'onChange' | 'value'
    > {
    value?: BoloOption | null;
    initialInputValue?: string;
    defaultValue: null;
    onChange?: OnChangeHandler;
    selectRef?: Ref<SelectInstance<unknown, false, GroupBase<unknown>> | undefined>;
}

export function mapBoloAutocompleteViewToOption(data: BoloAutocompleteView): BoloOption {
    const { boloId, title } = data;
    return {
        value: boloId,
        label: title,
        render: () => {
            return <div>{title}</div>;
        },
        data,
    };
}

type BoloAutocompleteActions =
    | { type: 'SET_OPTIONS'; options: BoloOption[] }
    | { type: 'SET_LOADING' }
    | { type: 'SET_ERROR'; error: string };

type BoloAutocompleteSelect =
    | { options: BoloOption[]; loading: false; error: undefined }
    | { options: BoloOption[]; loading: true; error: undefined }
    | { options: BoloOption[]; loading: false; error: string };

type OnChangeHandler = (value: BoloOption | null) => void;

const MINIMUM_SEARCH_STRING_LENGTH = 3;

export function BoloAutocomplete(props: BoloAutocompleteSelectProps): React.ReactElement {
    const apiClient = useApiClient();
    const [inputHasValue, setInputHasValue] = useState<boolean>(false);
    const { defaultValue, value, emptyState, selectRef, ...rest } = props;
    const hasFetchedInitialValue = useRef(false);

    const [state, dispatch] = React.useReducer(
        (
            state: BoloAutocompleteSelect,
            action: BoloAutocompleteActions
        ): BoloAutocompleteSelect => {
            switch (action.type) {
                case 'SET_OPTIONS':
                    return {
                        loading: false,
                        error: undefined,
                        options: action.options,
                    };
                case 'SET_LOADING':
                    return {
                        loading: true,
                        error: undefined,
                        options: [],
                    };
                case 'SET_ERROR':
                    return {
                        loading: false,
                        error: action.error,
                        options: [],
                    };
                default:
                    return state;
            }
        },
        { options: [], loading: false, error: undefined }
    );

    const { options } = state;
    const { onChange } = props;
    const onChangeMemo = React.useMemo(() => {
        return (
            e: Parameters<
                Exclude<React.ComponentProps<typeof BaseSelect>['onChange'], undefined>
            >[0]
        ) => {
            const selectedOption = options.find((option) => option.value === e.target.value);
            onChange?.(selectedOption ?? defaultValue);
        };
    }, [onChange, options, defaultValue]);

    const fetchOptions = React.useMemo(() => {
        return debounce(async (input: string, selectFirst?: boolean) => {
            if (input.trim().length < MINIMUM_SEARCH_STRING_LENGTH) {
                return;
            }
            try {
                dispatch({ type: 'SET_LOADING' });
                const boloAutocompleteResponse = await apiClient.request<BoloResource.BoloAutocomplete>(
                    {
                        method: 'POST',
                        data: {
                            query: input.trim(),
                        },
                        url: 'bolo/search/autocomplete',
                    }
                );
                if (boloAutocompleteResponse.data) {
                    const boloAutocompleteViews: BoloAutocompleteView[] =
                        boloAutocompleteResponse.data.data?.boloAutocompleteViews ?? [];

                    dispatch({
                        type: 'SET_OPTIONS',
                        options: boloAutocompleteViews.map(mapBoloAutocompleteViewToOption),
                    });

                    const prefillOption = boloAutocompleteViews?.[0];

                    if (selectFirst && prefillOption) {
                        onChange?.(mapBoloAutocompleteViewToOption(prefillOption));
                    }
                } else {
                    dispatch({ type: 'SET_OPTIONS', options: [] });
                }
            } catch (e) {
                dispatch({
                    type: 'SET_ERROR',
                    error: 'Failed to retrieve user options',
                });
            }
        }, 250);
    }, [apiClient, onChange]);

    const onInputChangeMemo = React.useMemo(() => {
        return (e: OnInputChangeParams[0], newValue: OnInputChangeParams[1]) => {
            setInputHasValue(!!newValue);
            void fetchOptions(newValue);
        };
    }, [fetchOptions]);

    useEffect(() => {
        if (props.initialInputValue && !hasFetchedInitialValue.current) {
            hasFetchedInitialValue.current = true;
            void fetchOptions(props.initialInputValue, true);
        }
    }, [fetchOptions, props.initialInputValue]);

    const focusManager = useFocusManager();

    return (
        <BaseSelect
            {...omitRef(rest)}
            filterOption={() => true}
            isLoading={state.loading}
            value={value ?? defaultValue}
            emptyState={inputHasValue ? 'No results found' : emptyState ?? 'Type to search'}
            options={state.options}
            isClearable={!isEqual(value, defaultValue)}
            onInputChange={onInputChangeMemo}
            onChange={(e) => {
                onChangeMemo(e);
                moveFocusNext(focusManager, e.currentTarget.value, false);
            }}
            matchSort={false}
            // @ts-expect-error ref type mismatch but it works
            ref={selectRef}
        />
    );
}
